// Projects Data Configuration

export interface MagazineProject {
  id: string;
  title: string;
  description: string;
  category: 'magazine';
  images: string[];
  pdfUrl: string;
  year: string;
  tools: string[];
}

export interface PosterProject {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  category: 'poster';
  image: string;
  year: string;
  tools: string[];
}

export interface BrandingProject {
  id: string;
  title: string;
  description: string;
  category: 'branding';
  coverImage: string;
  images: string[];
  pdfUrl?: string;
  year: string;
  tools: string[];
}

export type GraphicDesignProject = MagazineProject | PosterProject | BrandingProject;

// Magazine Projects
export const magazineProjects: MagazineProject[] = [
  {
    id: 'muse-magazine',
    title: 'Muse',
    description: 'A sophisticated editorial magazine featuring contemporary art and culture. The design emphasizes clean typography, balanced layouts, and thoughtful use of white space to let the content breathe.',
    category: 'magazine',
    images: [
      '/work/Graphic Design/magazine/Magazine_Muse/magazine_optimized_photos/magazine01.webp',
      '/work/Graphic Design/magazine/Magazine_Muse/magazine_optimized_photos/magazine02.webp',
      '/work/Graphic Design/magazine/Magazine_Muse/magazine_optimized_photos/magazine03.webp',
      '/work/Graphic Design/magazine/Magazine_Muse/magazine_optimized_photos/magazine04.webp',
      '/work/Graphic Design/magazine/Magazine_Muse/magazine_optimized_photos/magazine05.webp',
    ],
    pdfUrl: '/work/Graphic Design/magazine/Magazine_Muse/muse.pdf',
    year: '2025',
    tools: ['InDesign', 'Photoshop', 'Illustrator']
  }
];

// Poster Projects
export const posterProjects: PosterProject[] = [
  {
    id: 'boston-little-italy-travel',
    title: 'Boston Little Italy Travel Poster',
    shortTitle: 'Boston Little Italy Travel',
    description: 'A modern illustrated travel poster highlighting Boston\'s Little Italy, combining cultural landmarks, food, and transit design into a vibrant visual experience.',
    category: 'poster',
    image: "/work/Graphic Design/posters/Boston Little Italy Travel Poster'/ 1.webp",
    year: '2025',
    tools: ['Illustrator', 'Photoshop']
  },
  {
    id: 'valentines-card-making-flyer',
    title: 'Valentine\u2019s Card Making Event Flyer',
    shortTitle: 'Valentine\u2019s Card Making Event',
    description: 'A soft, romantic event flyer designed with warm tones and playful visuals to promote a community Valentine\u2019s Day activity.',
    category: 'poster',
    image: '/work/Graphic Design/posters/Valentine\u2019s Card Making Event Flyer/poster2.webp',
    year: '2025',
    tools: ['Illustrator', 'Photoshop']
  },
  {
    id: 'latin-dance-workshops',
    title: 'Latin Dance Workshops Poster',
    shortTitle: 'Latin Dance Workshops',
    description: 'A bold and energetic poster promoting dance workshops, blending tropical visuals and dynamic typography to capture movement and excitement.',
    category: 'poster',
    image: '/work/Graphic Design/posters/Latin Dance Workshops Poster/poster1.webp',
    year: '2025',
    tools: ['Illustrator', 'Photoshop']
  },
  {
    id: 'asian-american-diaspora',
    title: 'Asian American Diaspora – Learning Community Poster',
    shortTitle: 'Asian American Diaspora Learning Cluster',
    description: 'A promotional poster introducing a Spring 2026 learning community that connects research, writing, and cultural anthropology through immersive experiences in Boston\'s Chinatown.',
    category: 'poster',
    image: '/work/Graphic Design/posters/Asian American Diaspora – Learning Community Poster/image.png',
    year: '2025',
    tools: ['Illustrator', 'Photoshop']
  },
  {
    id: 'holiday-celebration',
    title: 'End-of-Semester Holiday Celebration Poster',
    shortTitle: 'Holiday Celebration Invitation',
    description: 'A festive invitation with warm holiday visuals, inviting students to a community celebration with an optional Yankee Swap activity.',
    category: 'poster',
    image: '/work/Graphic Design/posters/End-of-Semester Holiday Celebration Poster/image.png',
    year: '2025',
    tools: ['Illustrator', 'Photoshop']
  },
  {
    id: 'orientation-schedule',
    title: 'New International Student Orientation – Schedule Poster',
    shortTitle: 'Orientation Two-Day Schedule',
    description: 'A structured schedule design outlining the two-day orientation program for new international students, highlighting sessions, resources, and campus support.',
    category: 'poster',
    image: '/work/Graphic Design/posters/New International Student Orientation – Schedule Poster/International Community Day Poster/image.png',
    year: '2025',
    tools: ['Illustrator', 'InDesign']
  },
  {
    id: 'international-community-day',
    title: 'International Community Day Poster',
    shortTitle: 'International Community Day Announcement',
    description: 'A vibrant, inclusive poster inviting international students to learn about campus resources, safety practices, rights, and community support.',
    category: 'poster',
    image: '/work/Graphic Design/posters/International Community Day Poster/image.png',
    year: '2025',
    tools: ['Illustrator', 'Photoshop']
  },
  {
    id: 'orientation-wayfinding',
    title: 'Welcome to Orientation Wayfinding Poster',
    shortTitle: 'Orientation Welcome Sign',
    description: 'A clean, directional poster created to guide new international students on campus, emphasizing clarity and visual hierarchy.',
    category: 'poster',
    image: '/work/Graphic Design/posters/Welcome to Orientation Wayfinding Poster/image.png',
    year: '2025',
    tools: ['Illustrator']
  },
  {
    id: 'visual-hierarchy-typography',
    title: 'Visual Hierarchy - Typography Poster',
    shortTitle: 'Typography Study',
    description: 'An educational poster exploring the principles of visual hierarchy through typographic composition, demonstrating scale, weight, and spatial relationships.',
    category: 'poster',
    image: '/work/Graphic Design/posters/Visual Hierarchy - Typography Poster/image.png',
    year: '2025',
    tools: ['Illustrator', 'InDesign']
  }
];

// Product Design & Branding Projects
export const brandingProjects: BrandingProject[] = [
  {
    id: 'eja-lounge',
    title: 'EJA Lounge – Victorian-Inspired Restaurant',
    description: 'EJA Lounge is a complete branding and identity project inspired by Late Victorian aesthetics and Albanian cultural elements. The visual system blends ornate patterns, rich jewel-toned color palettes, and a signature fan motif to create an inviting, elegant atmosphere that reflects the lounge\'s name—"Eja," meaning "come" in Albanian. The project includes moodboards, typography selection, motifs, menu design, business cards, interior visualizations, and digital mockups, all unified through detailed Victorian-inspired ornamentation and modern refinement.',
    category: 'branding',
    coverImage: '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja01.webp',
    images: [
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja01.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja02.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja03.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja04.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja5.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja6.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja7.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja8.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja9.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja10.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja11.webp',
      '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja optimized photos/eja13.webp',
    ],
    pdfUrl: '/work/Graphic Design/product design & branding/EJA Lounge – Victorian-Inspired Restaurant Branding/Eja.pdf',
    year: '2025',
    tools: ['Adobe Illustrator', 'Adobe InDesign', 'Adobe Photoshop']
  },
  {
    id: 'malesia-tea',
    title: 'Malësia Tea',
    description: 'Complete brand identity and product packaging design for Malësia, an artisanal tea company. The design draws inspiration from Albanian mountain heritage, featuring elegant typography and natural color palettes.',
    category: 'branding',
    coverImage: '/work/Graphic Design/product design & branding/malesia/6.png',
    images: [
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia1.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia2.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia3.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia4.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia5.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia6.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia7.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia8.webp',
      '/work/Graphic Design/product design & branding/malesia/Malesia optimized photos/malesia9.webp',
      '/work/Graphic Design/product design & branding/malesia/10.png',
      '/work/Graphic Design/product design & branding/malesia/11.png',
      '/work/Graphic Design/product design & branding/malesia/12.png',
      '/work/Graphic Design/product design & branding/malesia/13.png',
    ],
    pdfUrl: '/work/Graphic Design/product design & branding/malesia/Malesia.pdf',
    year: '2025',
    tools: ['Illustrator', 'Photoshop', 'InDesign']
  }
];

// Photography curated selection (subset of gallery)
export const photographyPreview = [
  { id: 'photo-1', img: '/gallery/buildings/IMG_9737%202.jpeg', height: 450 },
  { id: 'photo-2', img: '/gallery/buildings/IMG_3672 2.jpeg', height: 380 },
  { id: 'photo-3', img: 'gallery/BHCC/image00070.jpeg', height: 450 },
  { id: 'photo-4', img: '/gallery/BHCC/image00104.jpeg', height: 450 },
  { id: 'photo-5', img: '/gallery/buildings/IMG_3401%202.jpeg', height: 380 },
  { id: 'photo-6', img: '/gallery/buildings/IMG_8906 2.jpeg', height: 450 },
];

// Drawings curated selection (subset of gallery)
export const drawingsPreview = [
  { id: 'draw-1', img: '/gallery/drawings/IMG_1664.jpeg', height: 400 },
  { id: 'draw-2', img: 'gallery/drawings/F8B0255A-4B8C-4703-881A-71AF5DD5642C.JPG', height: 345 },
  { id: 'draw-3', img: '/gallery/drawings/IMG_2017.jpeg', height: 400 },
  { id: 'draw-4', img: '/gallery/drawings/IMG_3423.jpeg', height: 400 },
  { id: 'draw-5', img: '/gallery/drawings/IMG_1143.jpeg', height: 365 },
  { id: 'draw-6', img: '/gallery/drawings/IMG_3596.jpg', height: 400 },
];
